<template>
  <div data-slot="rule-change-log-fields" class="flex flex-col gap-3">
    <form-step-tab
      v-if="isMobile"
      v-model="sideIndex"
      :items="mobileTabs"
      equal-width
    />

    <div v-if="!isMobile" class="flex gap-5 border-b border-border pb-2 text-sm text-muted-foreground">
      <div class="w-50 shrink-0" />
      <div class="min-w-0 flex-1 pl-3.5">{{ tr('Original value') }}</div>
      <div class="min-w-0 flex-1 pl-3.5">{{ tr('New value') }}</div>
    </div>

    <div
      v-for="item in entity.changes"
      :key="'field-item--' + item.field"
      :class="[ 'flex', isMobile ? 'flex-col gap-1.5' : 'gap-5' ]"
    >
      <div :class="[ 'text-sm text-foreground', isMobile ? 'font-semibold' : 'w-50 shrink-0 pt-3' ]">{{ tr(item.field) }}</div>

      <div
        v-for="column in visibleColumns"
        :key="column.side + '-' + item.field"
        class="glass-tile flex min-h-11 min-w-0 flex-1 flex-col justify-center rounded-lg px-3.5 py-3"
      >
        <template v-if="item.isCondition">
          <condition-definition
            v-if="hasConditions(item[column.side])"
            :item="item"
            :ad-account="entity?.rule?.ad_account"
            :side="column.side"
          />
          <span v-else class="self-center text-xs text-muted-foreground">{{ emptyLabel }}</span>
        </template>

        <template v-else-if="isArray(item[column.side])">
          <div
            v-for="(row, rowKey) in getDiffedArray(item[column.side], item[column.other])"
            :key="column.side + '-' + item.field + '-' + rowKey"
          >
            <span :class="valueItemClasses(column.side, row.highlightable)">{{ formatValue(row.value) }}</span>
          </div>
        </template>

        <div v-else>
          <span :class="valueItemClasses(column.side, true)">{{ formatValue(item[column.side]) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RuleChangeLogEntity from '@/entities/RuleChangeLog.js';
import ConditionDefinition from '@/ui/components/ConditionDefinition.vue';
import FormStepTab from '@/ui/components/FormStepTab.vue';
import { isConditionGroup } from '@/utils/conditions.js';

const SIDE_INDEX_ORIGINAL = 0;
const SIDE_INDEX_NEW = 1;

export default {
  name: 'RuleChangeLogFields',
  components: {
    ConditionDefinition,
    FormStepTab,
  },
  props: {
    entity: {
      type: RuleChangeLogEntity,
      required: true,
    },
  },
  data: () => ({
    sideIndex: SIDE_INDEX_NEW,
  }),
  computed: {
    isMobile() {
      return this.$root.isMobile;
    },
    mobileTabs() {
      return [
        this.tr('Original value'),
        this.tr('New value'),
      ];
    },
    visibleColumns() {
      const columns = [
        { side: 'original', other: 'new' },
        { side: 'new', other: 'original' },
      ];
      if (!this.isMobile) {
        return columns;
      }

      return [ columns[this.sideIndex === SIDE_INDEX_ORIGINAL ? 0 : 1] ];
    },
    emptyLabel() {
      return '- ' + this.tr('empty value') + ' -';
    },
  },
  methods: {
    valueItemClasses(side, changed) {
      return [
        'rounded px-1.5 py-0.5 text-xs leading-[1.8] [overflow-wrap:anywhere] [box-decoration-break:clone]',
        changed && (side === 'original' ? 'bg-destructive/10 text-destructive' : 'bg-success/15 text-success'),
      ];
    },
    hasConditions(value) {
      return isConditionGroup(value)
        && Array.isArray(value.items)
        && value.items.length > 0
        && !!value.items[0].field;
    },
    isArray(value) {
      return Array.isArray(value) && value.length > 0;
    },
    getDiffedArray(array1, array2) {
      const other = Array.isArray(array2) ? array2 : [];
      return array1.map(item => ({ value: item, highlightable: !other.includes(item) }));
    },
    formatValue(value) {
      if (value === null) {
        value = '';
      }
      else if (Array.isArray(value)) {
        if (value.length === 0) {
          value = '';
        }
        else if (value.length === 1) {
          value = value[0];
        }
      }
      else if (typeof value === 'string' && value.length !== 0) {
        value = this.tr(value);
      }
      else {
        value = value.toString();
      }

      switch (value) {
        case 'true':
          value = '✓ ' + this.tr('yes');
          break;

        case 'false':
          value = '✗ ' + this.tr('no');
          break;

        case '':
          value = this.emptyLabel;
          break;

        default:
          value = this.tr(value);
      }

      return value;
    },
  },
};
</script>
